import { Schema } from 'types/schema';

export interface SchemaNode {
  id: string;
  systemName: string;
  name: string;
  description: string | null;
  glyph: string | null;
  glyph_color: import('types/glyph').GlyphColor | null;
  isAbstract: boolean;
  children: SchemaNode[];
  localization_name_key?: string;
  localization_description_key?: string;
}

/**
 * Build a hierarchical tree of Schema objects from a flat list, using localized names/descriptions.
 * If rootSystemName is provided, returns only the subtree under that root; otherwise returns the full forest.
 *
 * @param schemas          Flat array of Schema objects.
 * @param levels           How many total levels to include (1 = roots only; default = 1).
 * @param locale           Locale to use for name/description lookups and sorting (e.g., 'en-US').
 * @param rootSystemName?  Optional system_name of the schema to treat as the single root.
 *                         If omitted, all top-level schemas become roots.
 * @returns                An array of SchemaNode objects (roots, each with nested children up to the given depth).
 */
export function getSchemaTree(
  schemas: Schema[],
  levels = 1,
  locale = 'en-US',
  rootSystemName?: string
): SchemaNode[] {
  // Helper: pick localized name or fall back to s.name
  const localizedName = (s: Schema): string => {
    return s.schema_name_translations?.[locale] ?? s.name;
  };

  const localizedDescription = (s: Schema): string | null => {
    return s.schema_description_translations?.[locale] ?? s.description;
  };

  // Build a map from schema_id to SchemaNode (children remain empty initially)
  const nodeMap = new Map<string, SchemaNode>();
  for (const s of schemas) {
    nodeMap.set(s.schema_id, {
      id: s.schema_id,
      systemName: s.system_name,
      name: localizedName(s),
      description: localizedDescription(s),
      glyph: s.glyph,
      glyph_color: s.glyph_color,
      isAbstract: s.is_abstract,
      children: [],
      localization_name_key: s.localization_name_key,
      localization_description_key: s.localization_description_key,
    });
  }

  // Attach each schema node to its parent, if present
  for (const s of schemas) {
    const parentId = s.parent_schema_id;
    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId)!.children.push(nodeMap.get(s.schema_id)!);
    }
  }

  // Sort each node’s children by localized name
  nodeMap.forEach((node) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name, locale));
  });

  // Recursive function to trim children beyond the specified depth
  function trim(node: SchemaNode, depth: number): SchemaNode {
    if (depth + 1 >= levels) {
      return { ...node, children: [] };
    }
    return {
      ...node,
      children: node.children.map((child) => trim(child, depth + 1)),
    };
  }

  // If a specific root is requested, return its trimmed subtree
  if (rootSystemName) {
    const rootEntry = Array.from(nodeMap.values()).find(
      (node) => node.systemName === rootSystemName
    );
    if (!rootEntry) {
      return [];
    }
    return [trim(rootEntry, 0)];
  }

  // Otherwise, collect all top-level schemas (no parent or parent not in nodeMap)
  const roots: SchemaNode[] = schemas
    .filter((s) => !s.parent_schema_id || !nodeMap.has(s.parent_schema_id))
    .map((s) => nodeMap.get(s.schema_id)!)
    .sort((a, b) => a.name.localeCompare(b.name, locale))
    .map((root) => trim(root, 0));

  return roots;
}
